import { useState, useRef, useCallback } from 'react';
import { MobileSelect } from '@/components/ui';
import { useAuthStore } from '@/features/auth';
import { useEffectiveBusinessId } from '@/hooks';
import { uploadReceipt } from '@/features/receipts/services/receiptService';
import { SupplierSelect } from '@/features/receipts/components/SupplierSelect';
import {
  RECEIPT_ACCEPTED_TYPES,
  SUPPLIER_COST_DOCUMENT_TYPES,
  formatFileSize,
} from '../types';

type DocumentType = 'receipt' | 'expense_invoice';

interface UploadFile {
  id: string;
  file: File;
  preview: string | null;
  fileType: 'image' | 'pdf';
  status: 'ready' | 'uploading' | 'uploaded' | 'error';
  error?: string;
  uploadProgress?: number;
  supplierId: string | null;
  documentType: DocumentType;
}

interface SupplierCostDocumentUploaderProps {
  projectId: string;
  onComplete?: () => void;
}

export function SupplierCostDocumentUploader({
  projectId,
  onComplete,
}: SupplierCostDocumentUploaderProps) {
  const [files, setFiles] = useState<UploadFile[]>([]);
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const { user } = useAuthStore();
  const businessId = useEffectiveBusinessId();

  const isValidFileType = (file: File): boolean => {
    return RECEIPT_ACCEPTED_TYPES.includes(file.type as typeof RECEIPT_ACCEPTED_TYPES[number]);
  };

  const getFileTypeCategory = (file: File): 'image' | 'pdf' => {
    if (file.type === 'application/pdf') return 'pdf';
    return 'image';
  };

  const generateId = (): string => {
    return `scd_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`;
  };

  const handleFileSelect = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFiles = e.target.files;
    if (!selectedFiles) return;

    const newFiles: UploadFile[] = [];
    const invalidFiles: string[] = [];

    Array.from(selectedFiles).forEach((file) => {
      if (!isValidFileType(file)) {
        invalidFiles.push(file.name);
        return;
      }

      const fileType = getFileTypeCategory(file);
      let preview: string | null = null;

      if (fileType === 'image') {
        preview = URL.createObjectURL(file);
      }

      newFiles.push({
        id: generateId(),
        file,
        preview,
        fileType,
        status: 'ready',
        supplierId: null,
        documentType: 'receipt',
      });
    });

    if (invalidFiles.length > 0) {
      setError(`Unsupported file types: ${invalidFiles.join(', ')}`);
    } else {
      setError(null);
    }

    setFiles((prev) => [...prev, ...newFiles]);

    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  }, []);

  const handleDrop = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    const droppedFiles = e.dataTransfer.files;
    if (!droppedFiles) return;

    const event = {
      target: { files: droppedFiles },
    } as React.ChangeEvent<HTMLInputElement>;
    handleFileSelect(event);
  }, [handleFileSelect]);

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
  };

  const removeFile = (id: string) => {
    setFiles((prev) => {
      const file = prev.find((f) => f.id === id);
      if (file?.preview) {
        URL.revokeObjectURL(file.preview);
      }
      return prev.filter((f) => f.id !== id);
    });
  };

  const updateFile = (id: string, updates: Partial<UploadFile>) => {
    setFiles((prev) =>
      prev.map((f) => (f.id === id ? { ...f, ...updates } : f))
    );
  };

  const handleUpload = async () => {
    if (!businessId || !user?.user_profile_id) {
      setError('Missing business or user context');
      return;
    }

    setIsUploading(true);
    setError(null);

    let hasErrors = false;

    for (const file of files) {
      if (file.status === 'uploaded') continue;

      updateFile(file.id, { status: 'uploading', uploadProgress: 0 });

      try {
        await uploadReceipt({
          file: file.file,
          businessId,
          uploadedBy: user.user_profile_id,
          projectId,
          supplierId: file.supplierId,
          documentType: file.documentType,
          source: 'web',
        });

        updateFile(file.id, { status: 'uploaded', uploadProgress: 100 });
      } catch (err) {
        console.error('Error uploading file:', err);
        updateFile(file.id, {
          status: 'error',
          error: err instanceof Error ? err.message : 'Upload failed',
        });
        hasErrors = true;
      }
    }

    setIsUploading(false);

    if (!hasErrors) {
      files.forEach((f) => {
        if (f.preview) URL.revokeObjectURL(f.preview);
      });
      setFiles([]);
      onComplete?.();
    }
  };

  const allFilesReady = files.length > 0 &&
    files.every((f) => f.status === 'ready');
  const allUploaded = files.length > 0 && files.every((f) => f.status === 'uploaded');

  const acceptTypes = RECEIPT_ACCEPTED_TYPES.join(',');

  return (
    <div className="space-y-4">
      {/* Drop Zone */}
      <div
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        className="border-2 border-dashed border-secondary-300 rounded-lg p-8 text-center hover:border-primary-500 transition-colors cursor-pointer"
        onClick={() => fileInputRef.current?.click()}
      >
        <input
          ref={fileInputRef}
          type="file"
          accept={acceptTypes}
          multiple
          onChange={handleFileSelect}
          className="hidden"
        />
        <svg
          className="w-12 h-12 mx-auto text-secondary-400 mb-3"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={1.5}
            d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12"
          />
        </svg>
        <p className="text-secondary-700 font-medium">
          Drop files here or tap to browse
        </p>
        <p className="text-sm text-secondary-500 mt-1">
          Receipts and invoices from suppliers
        </p>
      </div>

      {/* Error Message */}
      {error && (
        <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
          {error}
        </div>
      )}

      {/* File List */}
      {files.length > 0 && (
        <div className="space-y-4">
          {files.map((file) => (
            <div
              key={file.id}
              className="bg-white rounded-lg border border-secondary-200"
            >
              <div className="flex gap-4 p-4">
                {/* Preview */}
                <div className="w-20 h-20 flex-shrink-0 bg-secondary-100 rounded-lg overflow-hidden">
                  {file.fileType === 'image' && file.preview ? (
                    <img
                      src={file.preview}
                      alt={file.file.name}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-secondary-400">
                      <svg className="w-10 h-10" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8l-6-6zm-1 2l5 5h-5V4z" />
                      </svg>
                    </div>
                  )}
                </div>

                {/* Info */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <p className="font-medium text-secondary-900 truncate">
                        {file.file.name}
                      </p>
                      <p className="text-sm text-secondary-500">
                        {formatFileSize(file.file.size)}
                      </p>
                    </div>
                    {file.status !== 'uploading' && file.status !== 'uploaded' && (
                      <button
                        onClick={() => removeFile(file.id)}
                        className="p-1.5 text-secondary-400 hover:text-secondary-600 hover:bg-secondary-100 rounded-full transition-colors"
                      >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    )}
                  </div>

                  {/* Status Indicators */}
                  {file.status === 'uploading' && (
                    <div className="mt-2">
                      <div className="h-1.5 bg-secondary-200 rounded-full overflow-hidden">
                        <div
                          className="h-full bg-primary-500 transition-all duration-300"
                          style={{ width: `${file.uploadProgress || 0}%` }}
                        />
                      </div>
                      <p className="text-xs text-secondary-500 mt-1">Uploading...</p>
                    </div>
                  )}

                  {file.status === 'uploaded' && (
                    <div className="mt-2 flex items-center gap-1 text-green-600 text-sm">
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                      Uploaded
                    </div>
                  )}

                  {file.status === 'error' && (
                    <p className="mt-2 text-sm text-red-600">{file.error}</p>
                  )}
                </div>
              </div>

              {/* Options (only when ready) */}
              {file.status === 'ready' && (
                <div className="px-4 pb-4 space-y-3 border-t border-secondary-100 pt-3">
                  {/* Supplier */}
                  <SupplierSelect
                    value={file.supplierId}
                    onChange={(supplierId) =>
                      updateFile(file.id, { supplierId })
                    }
                    label="Supplier (optional)"
                    placeholder="Select supplier..."
                  />

                  {/* Document Type */}
                  <MobileSelect
                    label="Document Type"
                    value={file.documentType}
                    onChange={(value) =>
                      updateFile(file.id, { documentType: value as DocumentType })
                    }
                    options={SUPPLIER_COST_DOCUMENT_TYPES.map((t) => ({
                      value: t.value,
                      label: t.label,
                    }))}
                  />
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Upload Button */}
      {files.length > 0 && !allUploaded && (
        <button
          onClick={handleUpload}
          disabled={!allFilesReady || isUploading}
          className={`
            w-full py-3 px-4 rounded-lg font-medium transition-colors
            ${allFilesReady && !isUploading
              ? 'bg-primary-600 text-white hover:bg-primary-700'
              : 'bg-secondary-200 text-secondary-500 cursor-not-allowed'
            }
          `}
        >
          {isUploading ? 'Uploading...' : `Upload ${files.length} file${files.length > 1 ? 's' : ''}`}
        </button>
      )}

      {/* Done Button (after all uploaded) */}
      {allUploaded && (
        <button
          onClick={onComplete}
          className="w-full py-3 px-4 rounded-lg font-medium bg-green-600 text-white hover:bg-green-700 transition-colors"
        >
          Done
        </button>
      )}
    </div>
  );
}
